import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Wrench, Plus, Trash, X, ShieldCheck, Warning,
  CalendarBlank, CurrencyDollar, Tag
} from '@phosphor-icons/react';

const TYPES = ["AC", "Refrigerator", "Washing Machine", "Dishwasher", "Microwave", "Oven", "Water Purifier",
  "Geyser", "TV", "Fan", "Light", "Mixer/Grinder", "Vacuum Cleaner", "Air Purifier",
  "Inverter/UPS", "WiFi Router", "CCTV", "Doorbell", "Other"];
const ROOMS = ["Master Bedroom", "Bedroom 2", "Bedroom 3", "Living Room", "Kitchen", "Dining", "Bathroom", "Balcony", "Study", "Garage", "Garden", "Other"];

export default function AppliancesPage() {
  const { api, user } = useAuth();
  const [appliances, setAppliances] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showService, setShowService] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'Other', brand: '', model_number: '', room: '', purchase_date: '', warranty_expiry: '', purchase_price: '', serial_number: '', service_contact: '', notes: '' });
  const [svcForm, setSvcForm] = useState({ description: '', date: '', cost: '', technician: '' });
  const [filter, setFilter] = useState({ room: '', type: '' });

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.room) params.set('room', filter.room);
      if (filter.type) params.set('type', filter.type);
      const [appRes, sumRes] = await Promise.all([
        api.get(`/appliances?${params}`),
        api.get('/appliances/summary')
      ]);
      setAppliances(appRes.data);
      setSummary(sumRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [api, filter]);

  const handleAdd = async () => {
    if (!form.name) return;
    try {
      await api.post('/appliances', { ...form, purchase_price: parseFloat(form.purchase_price) || 0 });
      setShowAdd(false); setForm({ name: '', type: 'Other', brand: '', model_number: '', room: '', purchase_date: '', warranty_expiry: '', purchase_price: '', serial_number: '', service_contact: '', notes: '' });
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete appliance?')) return;
    try { await api.delete(`/appliances/${id}`); fetchData(); } catch (e) { console.error(e); }
  };

  const logService = async () => {
    if (!svcForm.description) return;
    try {
      await api.post(`/appliances/${showService}/service`, { ...svcForm, cost: parseFloat(svcForm.cost) || 0 });
      setShowService(null); setSvcForm({ description: '', date: '', cost: '', technician: '' });
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="appliances-page" className="space-y-6 relative">
      <div className="glow-orb-orange -top-20 -left-20 z-0" />

      {/* Header */}
      <div className="relative z-10 animate-fade-up flex items-start justify-between">
        <div>
          <p className="text-neutral-500 text-sm font-medium">Home Assets</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Appliances</h1>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-appliance-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#FF6B35] text-white text-xs font-semibold btn-glow">
          <Plus size={16} /> Add Appliance
        </button>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 relative z-10 animate-fade-up" style={{ animationDelay: '100ms' }}>
          <div className="glass-card p-4">
            <p className="overline mb-1">Total</p>
            <p className="text-xl font-heading font-bold text-white">{summary.total}</p>
          </div>
          <div className="glass-card p-4">
            <p className="overline mb-1">Active</p>
            <p className="text-xl font-heading font-bold text-green-400">{summary.active}</p>
          </div>
          <div className="glass-card p-4">
            <p className="overline mb-1">Warranty Soon</p>
            <p className="text-xl font-heading font-bold text-yellow-400">{summary.warranty_expiring_soon}</p>
          </div>
          <div className="glass-card p-4">
            <p className="overline mb-1">Total Value</p>
            <p className="text-lg font-heading font-bold text-white">Rs. {summary.total_value.toLocaleString('en-IN')}</p>
          </div>
          <div className="glass-card p-4">
            <p className="overline mb-1">Service Cost</p>
            <p className="text-lg font-heading font-bold text-red-400">Rs. {summary.total_service_cost.toLocaleString('en-IN')}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 relative z-10">
        <select value={filter.room} onChange={e => setFilter(f => ({ ...f, room: e.target.value }))}
          className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none">
          <option value="">All Rooms</option>
          {ROOMS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={filter.type} onChange={e => setFilter(f => ({ ...f, type: e.target.value }))}
          className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none">
          <option value="">All Types</option>
          {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Appliance Cards */}
      {appliances.length === 0 ? (
        <div className="glass-card p-10 text-center text-neutral-500 text-sm">No appliances registered yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10">
          {appliances.map((a, idx) => (
            <div key={a.appliance_id} className="glass-card p-5 animate-fade-up" data-testid={`appliance-${a.appliance_id}`}
              style={{ animationDelay: `${200 + idx * 50}ms` }}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-heading font-semibold text-white text-sm">{a.name}</h3>
                  <p className="text-[10px] text-neutral-500">{a.brand} {a.model_number} - {a.room}</p>
                </div>
                <span className="text-[9px] px-2 py-0.5 rounded-full bg-white/5 text-neutral-400">{a.type}</span>
              </div>
              {/* Warranty */}
              <div className="mb-3">
                {a.warranty_active ? (
                  <div className="flex items-center gap-1 text-[10px]">
                    <ShieldCheck size={12} className="text-green-400" />
                    <span className="text-green-400 font-medium">Warranty: {a.warranty_days_left}d left</span>
                  </div>
                ) : a.warranty_expiry ? (
                  <div className="flex items-center gap-1 text-[10px]">
                    <Warning size={12} className="text-red-400" />
                    <span className="text-red-400 font-medium">Warranty expired</span>
                  </div>
                ) : (
                  <span className="text-[10px] text-neutral-600">No warranty info</span>
                )}
              </div>
              {/* Details */}
              <div className="grid grid-cols-2 gap-2 text-[10px] mb-3">
                <div><span className="text-neutral-500">Purchased:</span> <span className="text-neutral-300">{a.purchase_date || '-'}</span></div>
                <div><span className="text-neutral-500">Price:</span> <span className="text-neutral-300">Rs. {a.purchase_price?.toLocaleString('en-IN') || 0}</span></div>
                {a.service_contact && <div className="col-span-2"><span className="text-neutral-500">Service:</span> <span className="text-neutral-300">{a.service_contact}</span></div>}
              </div>
              {/* Service history count */}
              {(a.service_history?.length || 0) > 0 && (
                <p className="text-[10px] text-neutral-500 mb-2">{a.service_history.length} service record(s)</p>
              )}
              {/* Actions */}
              <div className="flex items-center gap-2">
                <button onClick={() => { setShowService(a.appliance_id); setSvcForm({ description: '', date: '', cost: '', technician: '' }); }}
                  data-testid={`service-${a.appliance_id}`}
                  className="flex-1 flex items-center justify-center gap-1 text-[10px] py-1.5 rounded-lg bg-white/5 text-neutral-400 hover:bg-white/10">
                  <Wrench size={10} /> Log Service
                </button>
                {user?.role === 'admin' && (
                  <button onClick={() => handleDelete(a.appliance_id)} className="p-1.5 rounded-lg text-neutral-600 hover:text-red-400"><Trash size={12} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="glass-card w-full max-w-lg p-6 animate-fade-up max-h-[85vh] overflow-y-auto" data-testid="add-appliance-modal" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-white">Add Appliance</h3>
              <button onClick={() => setShowAdd(false)} className="text-neutral-500 hover:text-white"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="text" placeholder="Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="app-name"
                className="col-span-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none">
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input type="text" placeholder="Brand" value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))}
                className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              <select value={form.room} onChange={e => setForm(f => ({ ...f, room: e.target.value }))}
                className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none">
                <option value="">Room</option>
                {ROOMS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <input type="text" placeholder="Model Number" value={form.model_number} onChange={e => setForm(f => ({ ...f, model_number: e.target.value }))}
                className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              <input type="date" placeholder="Purchase Date" value={form.purchase_date} onChange={e => setForm(f => ({ ...f, purchase_date: e.target.value }))}
                className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              <input type="date" placeholder="Warranty Expiry" value={form.warranty_expiry} onChange={e => setForm(f => ({ ...f, warranty_expiry: e.target.value }))}
                className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              <input type="number" placeholder="Purchase Price" value={form.purchase_price} onChange={e => setForm(f => ({ ...f, purchase_price: e.target.value }))}
                className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              <input type="text" placeholder="Service Contact" value={form.service_contact} onChange={e => setForm(f => ({ ...f, service_contact: e.target.value }))}
                className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
            </div>
            <button onClick={handleAdd} data-testid="save-appliance"
              className="w-full mt-4 py-2.5 rounded-xl bg-[#FF6B35] text-white font-semibold text-sm btn-glow">Save Appliance</button>
          </div>
        </div>
      )}

      {/* Log Service Modal */}
      {showService && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowService(null)}>
          <div className="glass-card w-full max-w-sm p-6 animate-fade-up" data-testid="service-modal" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-white">Log Service</h3>
              <button onClick={() => setShowService(null)} className="text-neutral-500 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input type="text" placeholder="Description *" value={svcForm.description} onChange={e => setSvcForm(f => ({ ...f, description: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              <input type="date" value={svcForm.date} onChange={e => setSvcForm(f => ({ ...f, date: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              <input type="number" placeholder="Cost" value={svcForm.cost} onChange={e => setSvcForm(f => ({ ...f, cost: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              <input type="text" placeholder="Technician" value={svcForm.technician} onChange={e => setSvcForm(f => ({ ...f, technician: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
            </div>
            <button onClick={logService} data-testid="save-service"
              className="w-full mt-4 py-2.5 rounded-xl bg-[#FF6B35] text-white font-semibold text-sm btn-glow">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}
